"use client";
import { useState, useEffect, useRef } from "react";

/* ─── Data ────────────────────────────────────── */
const LAYERS = [
  {
    id: "intent", title: "Shared Intent", subtitle: "Alignment Layer",
    bullets: ["Align goals across agents", "Define success and constraints", "Coordinate decision-making"],
    accent: "#049FD9",
    border:  ["rgba(4,159,217,0.24)", "rgba(4,159,217,0.60)"],
    bg:      ["rgba(4,159,217,0.04)", "rgba(4,159,217,0.10)"],
    glow:    ["rgba(4,159,217,0.16)", "rgba(4,159,217,0.38)", "rgba(4,159,217,0.55)"],
    breatheKf: "v1-breathe-0",
    icon: (c: string) => (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <circle cx="10" cy="10" r="7.5" stroke={c} strokeWidth="1.5"/>
        <circle cx="10" cy="10" r="4"   stroke={c} strokeWidth="1.5"/>
        <circle cx="10" cy="10" r="1.5" fill={c}/>
      </svg>
    ),
  },
  {
    id: "context", title: "Shared Context", subtitle: "Memory Layer",
    bullets: ["Persistent shared memory", "Knowledge compounds over time", "Cross-agent awareness"],
    accent: "#22d3ee",
    border:  ["rgba(34,211,238,0.22)", "rgba(34,211,238,0.58)"],
    bg:      ["rgba(34,211,238,0.04)", "rgba(34,211,238,0.10)"],
    glow:    ["rgba(34,211,238,0.14)", "rgba(34,211,238,0.34)", "rgba(34,211,238,0.52)"],
    breatheKf: "v1-breathe-1",
    icon: (c: string) => (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <ellipse cx="10" cy="5.5" rx="6" ry="2.5" stroke={c} strokeWidth="1.4"/>
        <path d="M4 5.5v4c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5v-4" stroke={c} strokeWidth="1.4"/>
        <path d="M4 9.5v4c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5v-4" stroke={c} strokeWidth="1.4"/>
      </svg>
    ),
  },
  {
    id: "innovation", title: "Shared Reasoning", subtitle: "Consensus Layer",
    bullets: ["Agents build on each other", "New solutions emerge", "Collective reasoning"],
    accent: "#a78bfa",
    border:  ["rgba(167,139,250,0.24)", "rgba(167,139,250,0.62)"],
    bg:      ["rgba(167,139,250,0.05)", "rgba(167,139,250,0.12)"],
    glow:    ["rgba(167,139,250,0.18)", "rgba(167,139,250,0.40)", "rgba(167,139,250,0.58)"],
    breatheKf: "v1-breathe-2",
    icon: (c: string) => (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <path d="M10 2L12.5 7.5H18L13.5 11L15.5 17L10 13.5L4.5 17L6.5 11L2 7.5H7.5L10 2Z"
          stroke={c} strokeWidth="1.4" strokeLinejoin="round"/>
      </svg>
    ),
  },
] as const;

const DOTS = [[35,45],[140,25],[270,65],[400,38],[75,185],[215,155],[355,195],[455,145],[55,315],[195,285],[345,325],[450,285],[105,425],[275,405],[425,445]] as const;
const LINES = [[[35,45],[140,25]],[[140,25],[270,65]],[[270,65],[400,38]],[[75,185],[215,155]],[[215,155],[355,195]],[[35,45],[75,185]],[[140,25],[215,155]],[[270,65],[355,195]],[[75,185],[55,315]],[[215,155],[195,285]],[[355,195],[345,325]],[[55,315],[195,285]],[[195,285],[345,325]],[[345,325],[450,285]],[[105,425],[275,405]],[[275,405],[425,445]]] as const;

const HIT_DELAY = [0, 900, 1800];
const HIT_DUR   = 880;
const CONN_DUR  = 820;
const LOOP_MS   = 8000;

export function LayeredStackV1() {
  const [vis,      setVis]      = useState<any[]>([false, false, false]);
  const [pulsed,   setPulsed]   = useState<any[]>([false, false, false]);
  const [connKeys, setConnKeys] = useState<any[]>([0, 0]);
  const [hovered,  setHovered]  = useState<number | null>(null);
  const tids = useRef<ReturnType<typeof setTimeout>[]>([]);
  const loop  = useRef<ReturnType<typeof setInterval> | null>(null);
  const add   = (fn: () => void, ms: number) => { const id = setTimeout(fn, ms); tids.current.push(id); };

  const runPulse = () => {
    HIT_DELAY.forEach((delay, i) => {
      add(() => {
        setPulsed(p => { const n=[...p]; n[i]=true;  return n; });
        if (i < 2) setConnKeys(p => { const n=[...p]; n[i]=(p[i] || 0)+1; return n; });
        add(() => setPulsed(p => { const n=[...p]; n[i]=false; return n; }), HIT_DUR);
      }, delay);
    });
  };

  useEffect(() => {
    add(() => setVis([true,false,false]), 280);
    add(() => setVis([true,true,false]),  560);
    add(() => setVis([true,true,true]),   840);
    add(runPulse, 2000);
    loop.current = setInterval(runPulse, LOOP_MS);
    return () => { tids.current.forEach(clearTimeout); if (loop.current) clearInterval(loop.current); };
  }, []);

  return (
    <>
      <style>{`
        @keyframes v1-breathe-0 { 0%,100%{box-shadow:0 0 12px rgba(4,159,217,0.16)}   50%{box-shadow:0 0 24px rgba(4,159,217,0.34)} }
        @keyframes v1-breathe-1 { 0%,100%{box-shadow:0 0 12px rgba(34,211,238,0.14)}  50%{box-shadow:0 0 24px rgba(34,211,238,0.30)} }
        @keyframes v1-breathe-2 { 0%,100%{box-shadow:0 0 14px rgba(167,139,250,0.18)} 50%{box-shadow:0 0 28px rgba(167,139,250,0.38)} }
        @keyframes v1-conn-dot  { 0%{top:0;opacity:0.9} 100%{top:100%;opacity:0} }
        @keyframes v1-pulse-flash { 0%{opacity:0} 20%{opacity:0.3} 100%{opacity:0} }
      `}</style>

      <div style={{
        width:"100%", height:"100%", minHeight:480,
        background:"linear-gradient(155deg,#06080F 0%,#0A0C1A 55%,#080B14 100%)",
        fontFamily:"'Inter',system-ui,sans-serif",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
        padding:"16px 16px 16px 8px", position:"relative", overflow:"hidden",
        boxSizing:"border-box",
      }}>

        {/* bg radial */}
        <div style={{ position:"absolute",inset:0,pointerEvents:"none",
          background:"radial-gradient(ellipse 65% 55% at 50% 50%, rgba(4,159,217,0.07) 0%, rgba(167,139,250,0.05) 55%, transparent 75%)" }} />

        {/* bg dot network */}
        <svg style={{ position:"absolute",inset:0,width:"100%",height:"100%",opacity:0.09,pointerEvents:"none" }}>
          {DOTS.map(([x,y],k)=>
            <circle key={k} cx={x} cy={y} r="1.8" fill="#049FD9"/>)}
          {LINES.map(([[x1,y1],[x2,y2]],k)=>
            <line key={k} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#049FD9" strokeWidth="0.7"/>)}
        </svg>

        {/* stack */}
        <div style={{ width:"100%", maxWidth:424, position:"relative", zIndex:1 }}>
          {LAYERS.map((L, i) => {
            const lit   = pulsed[i] || hovered === i;
            const pulse = pulsed[i];

            return (
              <div key={L.id}>
                {/* CARD */}
                <div
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    borderRadius: 13,
                    border: `1px solid ${lit ? L.border[1] : L.border[0]}`,
                    background: lit ? L.bg[1] : L.bg[0],
                    overflow: "hidden",
                    opacity: vis[i] ? 1 : 0,
                    transform: vis[i] ? `scale(${hovered===i?1.018:1}) translateY(0)` : "translateY(16px)",
                    transition: vis[i]
                      ? "opacity 0.45s cubic-bezier(0.22,1,0.36,1), transform 0.35s cubic-bezier(0.22,1,0.36,1), border-color 0.2s, background 0.2s"
                      : "none",
                    animation: (!pulse && hovered!==i && vis[i])
                      ? `${L.breatheKf} ${9+i*1}s ease-in-out infinite` : "none",
                    boxShadow: pulse
                      ? `0 0 28px ${L.glow[2]}, inset 0 1px 0 rgba(255,255,255,0.10)`
                      : hovered===i
                      ? `0 0 20px ${L.glow[1]}, inset 0 1px 0 rgba(255,255,255,0.07)`
                      : undefined,
                    cursor: "default",
                    position: "relative",
                  }}
                >
                  {/* pulse flash overlay */}
                  <div style={{
                    position:"absolute", inset:0, pointerEvents:"none",
                    background:`radial-gradient(ellipse 90% 70% at 50% 40%, ${L.glow[2]} 0%, transparent 70%)`,
                    opacity: pulse ? 1 : 0,
                    animation: pulse ? `v1-pulse-flash ${HIT_DUR}ms ease-out forwards` : "none",
                  }} />

                  {/* HEADER ROW */}
                  <div style={{ display:"flex", alignItems:"center", gap:10, padding:"13px 15px 11px" }}>
                    {/* icon box */}
                    <div style={{
                      width:32, height:32, borderRadius:9, flexShrink:0,
                      background:`rgba(${L.accent === "#049FD9" ? "4,159,217" : L.accent === "#22d3ee" ? "34,211,238" : "167,139,250"},0.12)`,
                      border:`1px solid ${lit ? L.border[1] : L.border[0]}`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      color: L.accent,
                      boxShadow: lit ? `0 0 8px ${L.glow[1]}` : "none",
                      transition:"all 0.2s ease-out",
                    }}>
                      {L.icon(L.accent)}
                    </div>
                    {/* title */}
                    <p style={{ margin:0, flex:1, fontSize:13.5, fontWeight:600, color:"#fff", letterSpacing:"-0.01em", lineHeight:1.2 }}>
                      {L.title}
                    </p>
                    {/* badge */}
                    <span style={{
                      fontSize:8, padding:"3px 8px", borderRadius:999, flexShrink:0,
                      background:`rgba(${L.accent === "#049FD9" ? "4,159,217" : L.accent === "#22d3ee" ? "34,211,238" : "167,139,250"},0.13)`,
                      border:`1px solid ${lit ? L.border[1] : L.border[0]}`,
                      color:L.accent, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase",
                      transition:"all 0.2s",
                    }}>
                      {L.subtitle}
                    </span>
                  </div>

                  {/* divider */}
                  <div style={{ height:1, background:`linear-gradient(to right, transparent, ${L.border[0]} 20%, ${L.border[0]} 80%, transparent)`, margin:"0 15px" }} />

                  {/* BULLETS */}
                  <div style={{ padding:"10px 15px 13px", display:"flex", flexDirection:"column", gap:5 }}>
                    {L.bullets.map((b, bi) => (
                      <div key={bi} style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                        <div style={{ width:4, height:4, borderRadius:"50%", background:L.accent, flexShrink:0, marginTop:5, opacity:0.85 }} />
                        <span style={{ fontSize:11.5, color:"rgba(255,255,255,0.60)", lineHeight:1.5 }}>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CONNECTOR */}
                {i < 2 && (
                  <div style={{ display:"flex", justifyContent:"center", height:26, position:"relative" }}>
                    {/* static line */}
                    <div style={{
                      width:1.5, height:"100%",
                      background:`linear-gradient(to bottom, ${L.accent}50, ${LAYERS[i+1]?.accent || "#000"}50)`,
                    }} />
                    {/* animated dot */}
                    <div key={connKeys[i]} style={{
                      position:"absolute", left:"50%", top:0,
                      transform:"translateX(-50%)",
                      width:7, height:7, borderRadius:"50%",
                      background:`radial-gradient(circle, #fff 20%, ${L.accent} 60%, transparent 100%)`,
                      boxShadow:`0 0 8px 3px ${L.accent}`,
                      animation: connKeys[i] > 0 ? `v1-conn-dot ${CONN_DUR}ms ease-out forwards` : "none",
                      opacity: connKeys[i] > 0 ? undefined : 0,
                    }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </>
  );
}
